import { hash } from '@ember/helper';
import { on } from '@ember/modifier';
/* eslint-disable ember/no-computed-properties-in-native-classes */
import { action, computed } from '@ember/object';
import { LinkTo } from '@ember/routing';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  PrismCard,
  PrismIcon,
  PrismMetricBlock,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { and } from 'ember-truth-helpers';
import GeneralDemandLevel from 'lumen/components/general/demand-level';
import GeneralDropdownsDropdownArrowButton from 'lumen/components/general/dropdowns/dropdown-arrow-button';
import GeneralIconWithTooltip from 'lumen/components/general/icon-with-tooltip';
import HotelMarketInsightChartsChartAvailability from 'lumen/components/hotel/market-insight/charts/chart-availability';
import HotelMarketInsightChartsChartAvailabilityAlternativeLodging from 'lumen/components/hotel/market-insight/charts/chart-availability-alternative-lodging';
import HotelMarketInsightDemandLevelIndicator from 'lumen/components/hotel/market-insight/demand-level-indicator';
import HotelMarketInsightListsListEvents from 'lumen/components/hotel/market-insight/lists/list-events';
import HotelMarketInsightListsListItemsListItemEvent from 'lumen/components/hotel/market-insight/lists/list-items/list-item-event';
import HotelMarketInsightListsListItemsListItemHoliday from 'lumen/components/hotel/market-insight/lists/list-items/list-item-holiday';
import dayjsFormat from 'lumen/helpers/dayjs-format';

const NR_VISIBLE_EVENTS = 2;
const NR_TOTAL_EVENTS = 10;

interface HotelMarketInsightCardsDemandIndicatorsDetailsSignature {
  Args: {
    alternativeLodgingSummary: unknown;
    alternativeLodgingSummaryTooltipText: unknown;
    refDaysAgo: unknown;
    selectedDayModel: unknown;
    setHighlightedMarker: unknown;
    summaryForCurrentSelection: unknown;
  };
}

export default class HotelMarketInsightCardsDemandIndicatorsDetails extends Component<HotelMarketInsightCardsDemandIndicatorsDetailsSignature> {
  @service declare intl: Services['intl'];
  @service declare miAlternativeLodging: Services['mi-alternative-lodging'];
  @service declare miSelections: Services['mi-selections'];

  @tracked isAcceptedEventsExpanded = false;
  @tracked isHolidaysExpanded = false;
  @tracked isUnacceptedEventsExpanded = false;

  @computed('args.selectedDayModel.calendarEvents')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get acceptedEvents() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.args.selectedDayModel?.calendarEvents;
  }

  get formattedDemandForecastBenchmarkText(): string {
    return this.intl.t('market_insight.benchmark.demand', {
      levelText: this.intl
        .t(
          // @ts-expect-error: incorrect type
          `market_insight.rate_level.level${this.args.summaryForCurrentSelection.demandLevelColorScaleNumberDemandIndexBenchmark}`,
        )
        .toLowerCase(),
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      dow: this.args.selectedDayModel.date.format('dddd'),
    });
  }

  get formattedUnavailableAlternativeLodgingBenchmarkText(): string {
    return this.intl.t('market_insight.unavailability.benchmark', {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      unavailability:
        // @ts-expect-error: Incorrect type
        this.args.alternativeLodgingSummary
          .unavailableHomesBenchmarkPercentageNumber,
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      dow: this.args.selectedDayModel.date.format('dddd'),
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedUnavailableHotelsBenchmarkText() {
    return this.intl.t('market_insight.unavailability.benchmark', {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      unavailability:
        // @ts-expect-error: Incorrect type
        this.args.summaryForCurrentSelection
          .unavailableHotelsBenchmarkPercentageNumber,
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      dow: this.args.selectedDayModel.date.format('dddd'),
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hiddenAcceptedEvents() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return this.acceptedEvents.slice(NR_VISIBLE_EVENTS, NR_TOTAL_EVENTS);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hiddenHolidays() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return this.holidays.slice(NR_VISIBLE_EVENTS, NR_TOTAL_EVENTS);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hiddenUnacceptedEvents() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return this.unacceptedEvents.slice(NR_VISIBLE_EVENTS, NR_TOTAL_EVENTS);
  }

  @computed('args.selectedDayModel.holidays')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get holidays() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.args.selectedDayModel?.holidays;
  }

  @computed('args.selectedDayModel.unacceptedEvents')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get unacceptedEvents() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.args.selectedDayModel?.unacceptedEvents;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get visibleAcceptedEvents() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return this.acceptedEvents.slice(0, NR_VISIBLE_EVENTS);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get visibleHolidays() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return this.holidays.slice(0, NR_VISIBLE_EVENTS);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get visibleUnacceptedEvents() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return this.unacceptedEvents.slice(0, NR_VISIBLE_EVENTS);
  }

  @action
  toggleAcceptedEvents(): void {
    this.isAcceptedEventsExpanded = !this.isAcceptedEventsExpanded;
  }

  @action
  toggleHolidays(): void {
    this.isHolidaysExpanded = !this.isHolidaysExpanded;
  }

  @action
  toggleUnacceptedEvents(): void {
    this.isUnacceptedEventsExpanded = !this.isUnacceptedEventsExpanded;
  }

  <template>
    {{! @glint-nocheck }}
    <PrismCard
      @isParent={{true}}
      class="detail-pane-card"
      data-test-id="market-insight-demand-indicators-details"
    >
      <:header as |Header|>
        <Header.title>
          {{t "market_insight.demand_indicators"}}
        </Header.title>
      </:header>
      <:content>
        {{! Demand forecast card }}
        <PrismCard @isSecondary={{true}}>
          <:header>
            <div class="flex items-center gap-100">
              <p class="text-300--bold">{{t "demand.demand_forecast"}}</p>
              <GeneralIconWithTooltip
                @side="right"
                @tooltipContent={{t
                  "market_insight.kpi_explanation.submarket_demand"
                }}
                @tooltipTitle={{t "demand.demand_forecast"}}
              />
            </div>
          </:header>

          <:content>
            <div class="demand-forecast-metric-block flex flex-row">
              <PrismMetricBlock @align="left" class="mr-800 w-1/3">
                <:value>
                  <span class="text-300--bold">
                    {{@summaryForCurrentSelection.demandLevelLabel}}
                    <HotelMarketInsightDemandLevelIndicator
                      @params={{hash
                        demandLevelColorScaleNumber=@summaryForCurrentSelection.demandLevelColorScaleNumber
                      }}
                    />
                  </span>
                </:value>
                <:label>{{t "rates.occupancy.current"}}</:label>
              </PrismMetricBlock>

              {{#if @summaryForCurrentSelection.isLatest}}
                <PrismMetricBlock @align="left" class="mt-300 w-2/3">
                  <:value>
                    <span
                      class="text-300--bold
                        {{unless
                          @summaryForCurrentSelection.hasDemandChange
                          'non-actionable-value'
                        }}"
                    >
                      <div class="flex flex-row items-center gap-200">
                        {{#if @summaryForCurrentSelection.hasDemandChange}}
                          <GeneralDemandLevel
                            @demandLevel={{@summaryForCurrentSelection.demandLevelColorScaleNumberRefPeriod}}
                            @size="normal"
                          />
                          <PrismIcon @name="chevron-right" @size="small" />
                          <GeneralDemandLevel
                            @demandLevel={{@summaryForCurrentSelection.demandLevelColorScaleNumber}}
                            @size="normal"
                          />
                        {{else}}
                          {{t "overview_dashboard.kpi.no_change"}}
                        {{/if}}
                      </div>
                    </span>
                  </:value>
                  <:label>
                    <span
                      class={{unless
                        @summaryForCurrentSelection.hasDemandChange
                        "non-actionable-value"
                      }}
                      data-test-id="demand-view-forecast-daysago"
                    >{{t "general.daysago" count=@refDaysAgo}}</span>
                  </:label>
                </PrismMetricBlock>
              {{/if}}
            </div>

            {{#if
              @summaryForCurrentSelection.demandLevelColorScaleNumberDemandIndexBenchmark
            }}
              <p class="text-200--regular mt-300">
                {{this.formattedDemandForecastBenchmarkText}}
              </p>
            {{/if}}

          </:content>
        </PrismCard>

        {{! Accepted events card }}
        {{#if this.acceptedEvents.length}}
          <PrismCard
            @contentLayout="noPadding"
            @isSecondary={{true}}
            class="mt-400"
          >
            <:header>
              <p class="text-300--bold">{{t "events.accepted_events"}}</p>
            </:header>
            <:headerAfter>
              <LinkTo
                @query={{hash
                  month=(dayjsFormat @selectedDayModel.date format="YYYY-MM")
                }}
                @route="hotel.events.index"
                class="prism-link text-200--regular"
                target="_blank"
              >
                {{t "general.see_details"}}
              </LinkTo>
            </:headerAfter>
            <:content>
              <HotelMarketInsightListsListEvents
                @allEvents={{this.visibleAcceptedEvents}}
                @eventItemComponent={{HotelMarketInsightListsListItemsListItemEvent}}
                @hasLimitedHeight={{false}}
                @highlightMarker={{@setHighlightedMarker}}
              />
              {{#if this.hiddenAcceptedEvents.length}}
                <div class="expandable-footer">
                  <button
                    class="button--unstyled expandable-footer__dropdown"
                    type="button"
                    {{on "click" this.toggleAcceptedEvents}}
                  >
                    {{#if this.isAcceptedEventsExpanded}}
                      {{t "market_insight.events.hide"}}
                    {{else}}
                      {{t
                        "market_insight.events.view_more"
                        count=this.hiddenAcceptedEvents.length
                      }}
                    {{/if}}
                    <GeneralDropdownsDropdownArrowButton
                      @isOpen={{this.isAcceptedEventsExpanded}}
                    />
                  </button>
                  {{#if this.isAcceptedEventsExpanded}}
                    <div class="expandable-footer__content">
                      <HotelMarketInsightListsListEvents
                        @allEvents={{this.hiddenAcceptedEvents}}
                        @eventItemComponent={{HotelMarketInsightListsListItemsListItemEvent}}
                        @hasLimitedHeight={{false}}
                        @highlightMarker={{@setHighlightedMarker}}
                      />
                    </div>
                  {{/if}}
                </div>
              {{/if}}
            </:content>
          </PrismCard>
        {{/if}}

        {{! Unaccepted events card }}
        {{#if
          (and this.miSelections.showAllEvents this.unacceptedEvents.length)
        }}
          <PrismCard
            @contentLayout="noPadding"
            @isSecondary={{true}}
            class="mt-400"
            data-test-id="market-insight-demand-indicators-unaccepted-events"
          >
            <:header>
              <p class="text-300--bold">{{t
                  "market_insight.map.legend.top_unaccepted_events"
                }}</p>
            </:header>
            <:headerAfter>
              <LinkTo
                @query={{hash
                  month=(dayjsFormat @selectedDayModel.date format="YYYY-MM")
                }}
                @route="hotel.events.index"
                class="prism-link text-200--regular"
                target="_blank"
              >
                {{t "general.see_details"}}
              </LinkTo>
            </:headerAfter>
            <:content>
              <HotelMarketInsightListsListEvents
                @allEvents={{this.visibleUnacceptedEvents}}
                @eventItemComponent={{HotelMarketInsightListsListItemsListItemEvent}}
                @hasLimitedHeight={{false}}
                @highlightMarker={{@setHighlightedMarker}}
              />
              {{#if this.hiddenUnacceptedEvents.length}}
                <div class="expandable-footer">
                  <button
                    class="button--unstyled expandable-footer__dropdown"
                    type="button"
                    {{on "click" this.toggleUnacceptedEvents}}
                  >
                    {{#if this.isUnacceptedEventsExpanded}}
                      {{t "market_insight.events.hide"}}
                    {{else}}
                      {{t
                        "market_insight.events.view_more"
                        count=this.hiddenUnacceptedEvents.length
                      }}
                    {{/if}}
                    <GeneralDropdownsDropdownArrowButton
                      @isOpen={{this.isUnacceptedEventsExpanded}}
                    />
                  </button>
                  {{#if this.isUnacceptedEventsExpanded}}
                    <div class="expandable-footer__content">
                      <HotelMarketInsightListsListEvents
                        @allEvents={{this.hiddenUnacceptedEvents}}
                        @eventItemComponent={{HotelMarketInsightListsListItemsListItemEvent}}
                        @hasLimitedHeight={{false}}
                        @highlightMarker={{@setHighlightedMarker}}
                      />
                    </div>
                  {{/if}}
                </div>
              {{/if}}
            </:content>
          </PrismCard>
        {{/if}}

        {{! Holidays card }}
        {{#if this.holidays.length}}
          <PrismCard
            @contentLayout="noPadding"
            @isSecondary={{true}}
            class="mt-400"
          >
            <:header>
              <p class="text-300--bold">{{t "events.holidays"}}</p>
            </:header>
            <:headerAfter>
              <LinkTo
                @query={{hash
                  month=(dayjsFormat @selectedDayModel.date format="YYYY-MM")
                }}
                @route="hotel.events.index"
                class="prism-link text-200--regular"
                target="_blank"
              >
                {{t "general.see_details"}}
              </LinkTo>
            </:headerAfter>
            <:content>
              <HotelMarketInsightListsListEvents
                @allEvents={{this.visibleHolidays}}
                @eventItemComponent={{HotelMarketInsightListsListItemsListItemHoliday}}
                @hasLimitedHeight={{false}}
                @highlightMarker={{false}}
              />
              {{#if this.hiddenHolidays.length}}
                <div class="expandable-footer">
                  <button
                    class="button--unstyled expandable-footer__dropdown"
                    type="button"
                    {{on "click" this.toggleHolidays}}
                  >
                    {{#if this.isHolidaysExpanded}}
                      {{t "market_insight.holidays.hide"}}
                    {{else}}
                      {{t "market_insight.holidays.view_more"}}
                    {{/if}}
                    <GeneralDropdownsDropdownArrowButton
                      @isOpen={{this.isHolidaysExpanded}}
                    />
                  </button>
                  {{#if this.isHolidaysExpanded}}
                    <div class="expandable-footer__content">
                      <HotelMarketInsightListsListEvents
                        @allEvents={{this.hiddenHolidays}}
                        @eventItemComponent={{HotelMarketInsightListsListItemsListItemHoliday}}
                        @hasLimitedHeight={{false}}
                        @highlightMarker={{false}}
                      />
                    </div>
                  {{/if}}
                </div>
              {{/if}}
            </:content>
          </PrismCard>
        {{/if}}

        {{! Unavailable hotels card }}
        {{#if @summaryForCurrentSelection}}
          <PrismCard @isSecondary={{true}} class="mt-400">
            <:header>
              <div class="flex items-center gap-100">
                <p class="text-300--bold">{{t
                    "market_insight.availability.unavailable_hotels"
                  }}</p>
                <GeneralIconWithTooltip
                  @side="right"
                  @tooltipContent={{t
                    "market_insight.kpi_explanation.unavailable_hotels"
                  }}
                  @tooltipTitle={{t
                    "market_insight.availability.unavailable_hotels"
                  }}
                />
              </div>
            </:header>

            <:content>
              <div class="mt-300">
                <HotelMarketInsightChartsChartAvailability
                  @summary={{@summaryForCurrentSelection}}
                />

                {{#if
                  @summaryForCurrentSelection.unavailableHotelsBenchmarkPercentageNumber
                }}
                  <p class="text-200--regular mt-400">
                    {{this.formattedUnavailableHotelsBenchmarkText}}
                  </p>
                {{/if}}
              </div>
            </:content>
          </PrismCard>
        {{/if}}

        {{! Unavailable alt. hotels card }}
        {{#if
          (and
            @alternativeLodgingSummary
            this.miAlternativeLodging.isAltLodgingAirBnbSupported
          )
        }}
          <PrismCard @isSecondary={{true}} class="mt-400">
            <:header>
              <div class="flex items-center gap-100">
                <p class="text-300--bold">{{t
                    "market_insight.availability.alt_lodgings.unavailable_alt_lodging"
                  }}</p>
                <GeneralIconWithTooltip
                  @side="right"
                  @tooltipContent={{@alternativeLodgingSummaryTooltipText}}
                  @tooltipTitle={{t
                    "market_insight.availability.alt_lodgings.unavailable_alt_lodging"
                  }}
                />
              </div>
            </:header>

            <:content>
              <div class="mt-300">
                <HotelMarketInsightChartsChartAvailabilityAlternativeLodging
                  @summary={{@alternativeLodgingSummary}}
                />

                {{#if
                  @alternativeLodgingSummary.unavailableHomesBenchmarkPercentageNumber
                }}
                  <p class="text-200--regular mt-300">
                    {{this.formattedUnavailableAlternativeLodgingBenchmarkText}}
                  </p>
                {{/if}}
              </div>
            </:content>
          </PrismCard>
        {{/if}}
      </:content>
    </PrismCard>
  </template>
}
